import { useId } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@voll-intelligence/ui-kit/ui/select';
import {
  FIELD_CONTROL_SIZE,
  FieldShell,
  fieldMessageId,
  fieldToneBorder,
  type FieldShellExtras,
} from './field_shell.js';

/**
 * O CAMPO DE SELEÇÃO de catálogo FECHADO, no `Select` do DS — o irmão síncrono
 * do `AsyncSelectField`: as opções já estão na mão, então não há busca, termo
 * mínimo nem estado de voo; abrir mostra a lista inteira.
 *
 * Substitui o `SelectField` legado (que é um `<select>` nativo vestido pelas
 * folhas `--v2-*`) um host por vez. É a metade síncrona da mesma fileira do
 * checkout: sem ela, o campo de centro de custo em tokens do DS ficava entre
 * dois selects legados 4px mais altos, com raio e borda de outra família.
 *
 * Herda o nome `SelectField` quando o último consumidor do legado morrer.
 */

interface StaticSelectOption {
  value: string;
  label: string;
}

export interface StaticSelectFieldProps extends FieldShellExtras {
  /** Nome visível E acessível do campo. */
  label: string;
  /** Vazio = nada escolhido: cai no `placeholder` do gatilho. */
  value: string;
  onChange: (value: string) => void;
  options: StaticSelectOption[];
  placeholder?: string;
  id?: string;
  disabled?: boolean;
}

export function StaticSelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  id,
  disabled,
  adornment,
  adornmentLabel,
  message,
  messageTone,
  required,
}: Readonly<StaticSelectFieldProps>) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  /* `items` é o mapa valor→rótulo que o `SelectValue` usa pra imprimir a
     ESCOLHA sem o consumidor repetir a lista no gatilho. */
  const items = Object.fromEntries(options.map((o) => [o.value, o.label]));
  return (
    <FieldShell
      label={label}
      fieldId={fieldId}
      adornment={adornment}
      adornmentLabel={adornmentLabel}
      message={message}
      messageTone={messageTone}
    >
      <Select
        items={items}
        /* `null` e não `''`: no Base UI o vazio é a ausência de valor — string
           vazia procuraria um item com esse valor e o gatilho ficaria em branco
           em vez de mostrar o placeholder. */
        value={value || null}
        onValueChange={(next) => {
          onChange(typeof next === 'string' ? next : '');
        }}
        disabled={disabled}
      >
        <SelectTrigger
          id={fieldId}
          className={`${FIELD_CONTROL_SIZE}${fieldToneBorder({ message, messageTone })}`}
          aria-describedby={message ? fieldMessageId(fieldId) : undefined}
          aria-required={required || undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        {/* Ancorado ABAIXO do campo, como o painel do `AsyncSelectField` ao lado —
            e não no `alignItemWithTrigger` que o kit traz ligado por padrão, que
            é o modo macOS: sobrepõe o gatilho alinhando o item escolhido a ele.
            Numa fileira onde o campo vizinho abre pra baixo, dois gestos
            diferentes pra mesma pergunta. `false` também devolve a animação de
            entrada (o preset desliga com `data-[align-trigger=true]`), então os
            dois campos passam a abrir igual. */}
        <SelectContent alignItemWithTrigger={false} align="start">
          <SelectGroup>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </FieldShell>
  );
}
